import React, { useEffect, useState } from "react";
import { Facebook, Link2, Unlink, Send, ExternalLink, AlertCircle, CheckCircle2, Loader2, Image as ImageIcon, Eye, EyeOff, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";

// Poster picker — combines the Grand Opening posters with the new
// "Now Open Under New Ownership" flyers. All four sizes can be attached
// to a Facebook post AND downloaded individually for printed hard copies.
// PDFs are bundled separately for printing-shop handouts.
const POSTERS = [
  { url: "/assets/marketing/new-mgmt-square.png", label: "Oroquieta Branch · Square 1080×1080 (FB feed)" },
  { url: "/assets/marketing/new-mgmt-story.png",  label: "Oroquieta Branch · Story 1080×1920 (FB/IG Story)" },
  { url: "/assets/marketing/new-mgmt-a4.png",      label: "Oroquieta Branch · A4 Printable PNG (2480×3508)" },
  { url: "/assets/marketing/new-mgmt-a4.pdf",      label: "Oroquieta Branch · A4 PDF (for print shop)" },
  { url: "/assets/marketing/new-mgmt-a4-4up.pdf",  label: "Oroquieta Branch · A4 PDF · 4 handouts per sheet (cut to A6)" },
  { url: "/assets/marketing/new-mgmt-social.pdf",  label: "Oroquieta Branch · Social PDF (square + story)" },
  { url: "/assets/marketing/grand-opening-square.png", label: "Grand Opening · Square 1080×1080" },
  { url: "/assets/marketing/grand-opening-story.png",  label: "Grand Opening · Story 1080×1920" },
  { url: "/assets/marketing/grand-opening-link.png",   label: "Grand Opening · Link 1200×630" },
];

// Tiny helper that triggers a browser download of the chosen asset.
const downloadAsset = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || url.split("/").pop();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const SAMPLE_COPY = `🎉 NOW OPEN — UNDER NEW OWNERSHIP 🎉

Arbe Minimart is back, fresher than ever! 🛒 Drop by and meet the new owners — we'd love to welcome you in.

✅ FARM FRESH meats — FRESH FROM OUR OWN FARM, NEVER imported
✅ Premium PORK, BEEF, CHICKEN cuts daily
✅ Groceries, condiments, frozen products & Sunjin feeds
✅ Order online at arbeminimart.com — FREE delivery on orders over ₱1,000
✅ Pay via GCash, Maya, Bank Transfer, PayPal or COD

📍 Felix Taghap Street, Poblacion 2, Oroquieta City 7207
📞 0920 297 1717 / 0920 279 9298

Come visit us today — Salamat po! 🙏🥩🥩

#ArbeMinimart #NowOpen #UnderNewOwnership #OroquietaCity #FarmFreshMeats #LocallySourced #SukiNa`;

export default function AdminFacebook() {
  const [status, setStatus] = useState(null);
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState(SAMPLE_COPY);
  const [imageUrl, setImageUrl] = useState("");
  const [posting, setPosting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/facebook/status");
      setStatus(data);
    } catch { setStatus({ connected: false }); }
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get("/facebook/posts");
      setHistory(data.items || []);
    } catch { /* noop */ }
  };

  useEffect(() => { loadStatus(); loadHistory(); }, []);

  const connect = async () => {
    if (!pageId || !token) { toast.error("Enter both Page ID and Access Token"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post("/facebook/connect", { page_id: pageId.trim(), access_token: token.trim() });
      toast.success(`Connected to ${data.page_name}`);
      setToken(""); setPageId("");
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Facebook Page? You'll need to paste the token again to reconnect.")) return;
    try {
      await api.post("/facebook/disconnect", {});
      toast.success("Disconnected");
      setStatus({ connected: false });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async () => {
    if (!message) { toast.error("Write a post message first"); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/facebook/post", { message, image_url: imageUrl || null });
      toast.success("Published to Facebook!");
      if (data.permalink) window.open(data.permalink, "_blank");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  const isConnected = status?.connected;

  return (
    <div className="space-y-6" data-testid="admin-facebook">
      {/* Marketing Materials — always visible, no FB connection required.
          Lets the owner download printable flyers + the FB-ready images
          straight from this page, then either share or print them. */}
      <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5" data-testid="marketing-materials">
        <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-1">
          <ImageIcon className="h-5 w-5 text-[#E67E22]" /> Marketing Materials
        </div>
        <p className="text-xs text-[#738380] mb-3">
          Tap a flyer to download it. Use the A4 version for printing handouts, the square / story versions for Facebook &amp; Instagram.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {POSTERS.map((p) => {
            const isPdf = p.url.toLowerCase().endsWith(".pdf");
            return (
              <div key={`dl-${p.url}`} className="border border-[#E1E5DF] rounded-xl p-2 hover:border-[#2F5233]/50 transition-all" data-testid={`mm-card-${p.url.split("/").pop()}`}>
                {isPdf ? (
                  <div className="w-full h-32 rounded-md bg-[#FBF6E9] border border-[#E1E5DF] flex flex-col items-center justify-center gap-1 text-[#7A4A1F]">
                    <Download className="h-7 w-7" />
                    <span className="text-[10px] font-bold tracking-wide uppercase">PDF · ready to print</span>
                  </div>
                ) : (
                  <img src={p.url} alt={p.label} className="w-full h-32 object-cover rounded-md bg-[#F1F3EE]" />
                )}
                <div className="text-[11px] font-semibold mt-1.5 text-[#1C2826] leading-tight min-h-[28px]">{p.label}</div>
                <Button
                  size="sm"
                  onClick={() => {
                    downloadAsset(p.url, p.url.split("/").pop());
                    toast.success(isPdf ? "PDF downloaded — take it to a print shop" : "Flyer downloaded — ready to print or share");
                  }}
                  className="mt-1.5 w-full h-9 bg-[#2F5233] hover:bg-[#1F3D24] text-white text-xs"
                  data-testid={`mm-download-${p.url.split("/").pop()}`}
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Connection */}
      <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
        <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
          <Facebook className="h-5 w-5 text-[#1877F2]" /> Facebook Page Connection
        </div>

        {isConnected ? (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-[#E8F5EE] border border-[#B5D9C2] rounded-xl">
            <CheckCircle2 className="h-5 w-5 text-[#2F5233] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-[#1C2826]" data-testid="fb-status-page-name">{status.page_name}</div>
              <div className="text-xs text-[#4A5D5A]">Page ID: <span className="font-mono">{status.page_id}</span> · Connected since {(status.connected_at || "").slice(0, 10)}</div>
            </div>
            <Button variant="outline" size="sm" onClick={disconnect} className="border-[#C62828] text-[#C62828] hover:bg-[#C62828] hover:text-white" data-testid="fb-disconnect">
              <Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {status?.error && (
              <div className="flex items-start gap-2 p-3 bg-[#FBE7E7] border border-[#F1B8B8] rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 text-[#C62828] flex-shrink-0 mt-0.5" />
                <div className="text-[#C62828]">{status.error}</div>
              </div>
            )}
            <div className="bg-[#F9F9F7] border border-[#E1E5DF] rounded-lg p-3 text-xs text-[#4A5D5A] leading-relaxed">
              <div className="font-bold text-[#1C2826] text-sm mb-1">How to get your Page Access Token (5 min):</div>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Go to <a className="text-[#1877F2] underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> and create a new <strong>Business</strong> app named "Arbeminimart Poster"</li>
                <li>Add the <strong>Facebook Login</strong> product to your app (any settings)</li>
                <li>Open <a className="text-[#1877F2] underline" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Graph API Explorer</a>, choose your app, click "Generate Access Token"</li>
                <li>Tick permissions: <span className="font-mono">pages_show_list</span>, <span className="font-mono">pages_manage_posts</span>, <span className="font-mono">pages_read_engagement</span>, <span className="font-mono">publish_video</span></li>
                <li>Submit the request — copy the short-lived token</li>
                <li>In Graph API Explorer, query <span className="font-mono">/me/accounts</span> — find the "Arbeminimart" entry and copy its <span className="font-mono">access_token</span> (this one never expires) and <span className="font-mono">id</span></li>
                <li>Paste both into the form below</li>
              </ol>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Facebook Page ID</Label>
                <Input value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="e.g. 102345678901234" className="mt-1.5 h-11 font-mono text-sm" data-testid="fb-page-id" />
              </div>
              <div>
                <Label>Page Access Token (long-lived)</Label>
                <div className="relative mt-1.5">
                  <Input
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    type={showToken ? "text" : "password"}
                    placeholder="EAAI..."
                    className="h-11 pr-10 font-mono text-xs"
                    data-testid="fb-page-token"
                  />
                  <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#738380] hover:text-[#1C2826]">
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <Button onClick={connect} disabled={connecting} className="bg-[#1877F2] hover:bg-[#0F61CE] text-white" data-testid="fb-connect">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              {connecting ? "Validating with Facebook..." : "Connect Facebook Page"}
            </Button>
          </div>
        )}
      </div>

      {/* Composer */}
      {isConnected && (
        <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5" data-testid="fb-composer">
          <div className="flex items-center gap-2 text-[#1C2826] font-heading font-bold text-lg mb-3">
            <Send className="h-5 w-5 text-[#1877F2]" /> Compose Post
          </div>

          <div className="space-y-4">
            <div>
              <Label>Post message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={10}
                className="mt-1.5 font-sans text-sm"
                placeholder="What's on your mind?"
                data-testid="fb-message"
              />
              <div className="text-[11px] text-[#738380] mt-1 text-right">{message.length} / 63,206 chars</div>
            </div>

            <div>
              <Label>Attach an image (optional)</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setImageUrl("")}
                  className={`border-2 rounded-xl p-3 text-left transition-all ${!imageUrl ? "border-[#2F5233] bg-[#F1F5EF]" : "border-[#E1E5DF] hover:border-[#2F5233]/50"}`}
                  data-testid="fb-image-none"
                >
                  <div className="font-heading font-bold text-sm text-[#1C2826]">Text only</div>
                  <div className="text-[11px] text-[#738380] mt-0.5">No image</div>
                </button>
                {POSTERS.map((p) => (
                  <div
                    key={p.url}
                    className={`relative border-2 rounded-xl p-2 text-left transition-all ${imageUrl === p.url ? "border-[#2F5233] bg-[#F1F5EF]" : "border-[#E1E5DF] hover:border-[#2F5233]/50"}`}
                    data-testid={`fb-image-${p.url.split("/").pop()}`}
                  >
                    <button
                      type="button"
                      onClick={() => setImageUrl(p.url)}
                      className="block w-full text-left"
                    >
                      <img src={p.url} alt={p.label} className="w-full h-24 object-cover rounded-md" />
                      <div className="text-[11px] font-semibold mt-1.5 text-[#1C2826] leading-tight pr-7">{p.label}</div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadAsset(p.url, p.url.split("/").pop());
                        toast.success("Flyer downloaded — ready to print or share");
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-white border border-[#E1E5DF] text-[#2F5233] hover:bg-[#2F5233] hover:text-white shadow-sm"
                      title="Download this flyer"
                      data-testid={`fb-download-${p.url.split("/").pop()}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="Or paste a custom image URL or /assets/ path"
                  className="h-9 text-xs font-mono"
                  data-testid="fb-image-custom"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#E1E5DF]">
              <div className="text-xs text-[#738380] flex items-center gap-1">
                <ImageIcon className="h-3.5 w-3.5" />
                {imageUrl ? "Image post" : "Text-only post"} · will publish to <span className="font-bold text-[#1C2826]">{status.page_name}</span>
              </div>
              <Button onClick={publish} disabled={posting || !message} className="bg-[#1877F2] hover:bg-[#0F61CE] text-white" data-testid="fb-publish">
                {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {posting ? "Publishing..." : "Publish to Facebook"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {isConnected && history.length > 0 && (
        <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5">
          <div className="text-[#1C2826] font-heading font-bold text-lg mb-3">Recent Posts ({history.length})</div>
          <div className="space-y-2">
            {history.map((p) => (
              <div key={p.id} className="flex items-start gap-3 p-3 border border-[#E1E5DF] rounded-lg hover:bg-[#F9F9F7]" data-testid={`fb-post-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt="" className="h-12 w-12 rounded-md object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1C2826] line-clamp-2">{p.message}</div>
                  <div className="text-[11px] text-[#738380] mt-1 flex items-center gap-2">
                    <span>{(p.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span>·</span>
                    <span className="uppercase tracking-wider font-semibold">{p.type}</span>
                    {p.permalink && (
                      <>
                        <span>·</span>
                        <a href={p.permalink} target="_blank" rel="noreferrer" className="text-[#1877F2] hover:underline flex items-center gap-1">
                          View on Facebook <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
